import { useEffect, useMemo, useState } from "react";
import { 
  Box, 
  Card, 
  Grid, 
  Stack, 
  styled, 
  useTheme,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  Collapse,
  IconButton,
  InputAdornment,
  Skeleton,
  alpha,
  Checkbox,
  Chip,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import { 
  FilterList, 
  ExpandMore, 
  Refresh, 
  CalendarToday,
  Payments,
  AccountBalanceWallet,
  Savings,
  WarningAmber,
  CreditCard,
  People,
  ShoppingCart,
  Article,
  ContactMail,
  Agriculture,
  TrendingUp,
  TrendingDown,
} from "@mui/icons-material";
import Chart from "react-apexcharts";
import { Small, Tiny } from "components/Typography";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";

const StyledChart = styled(Chart)(({ theme }) => ({
  "& .apexcharts-tooltip": {
    boxShadow: "none",
  },
  "& .apexcharts-tooltip.apexcharts-theme-light": {
    border: "none",
    color: theme.palette.text.primary,
    borderRadius: 8,
  },
}));

// Styled components for filters
const FilterContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: "visible",
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 14,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.75, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  borderRadius: "14px 14px 0 0",
}));

const FilterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
}));

const DatePickerField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
    },
  },
  "& .MuiInputBase-input": {
    cursor: "pointer",
    colorScheme: theme.palette.mode === "dark" ? "dark" : "light",
  },
  '& input[type="date"]': {
    colorScheme: theme.palette.mode === "dark" ? "dark" : "light",
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    filter: theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
    cursor: "pointer",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 10,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  boxShadow: "none",
  minHeight: 40,
  "&:hover": {
    boxShadow: "none",
  },
}));

const DashboardSectionCard = styled(Card)(({ theme }) => ({
  borderRadius: 14,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  backgroundImage: "none",
  height: "100%",
}));

const StatusChip = styled(Typography)(({ theme, statuscolor }) => ({
  fontWeight: 600,
  borderRadius: 999,
  padding: theme.spacing(0.35, 1.25),
  fontSize: "0.75rem",
  backgroundColor: statuscolor ? alpha(statuscolor, 0.12) : alpha(theme.palette.grey[500], 0.1),
  color: statuscolor || theme.palette.grey[700],
  border: `1px solid ${statuscolor ? alpha(statuscolor, 0.22) : alpha(theme.palette.grey[500], 0.2)}`,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
}));

const getMetricVisual = (title = "") => {
  const key = String(title).trim().toLowerCase();
  if (key.includes("revenue")) {
    return { icon: Payments, color: "#2563EB", bg: alpha("#2563EB", 0.12) };
  }
  if (key.includes("full payment")) {
    return { icon: AccountBalanceWallet, color: "#7C3AED", bg: alpha("#7C3AED", 0.12) };
  }
  if (key.includes("advance")) {
    return { icon: Savings, color: "#EA580C", bg: alpha("#EA580C", 0.12) };
  }
  if (key.includes("outstanding")) {
    return { icon: WarningAmber, color: "#DC2626", bg: alpha("#DC2626", 0.12) };
  }
  if (key.includes("credit")) {
    return { icon: CreditCard, color: "#0D9488", bg: alpha("#0D9488", 0.12) };
  }
  if (key.includes("farmer")) {
    return { icon: People, color: "#2563EB", bg: alpha("#2563EB", 0.12) };
  }
  if (key.includes("order")) {
    return { icon: ShoppingCart, color: "#7C3AED", bg: alpha("#7C3AED", 0.12) };
  }
  if (key.includes("post")) {
    return { icon: Article, color: "#EA580C", bg: alpha("#EA580C", 0.12) };
  }
  if (key.includes("enquir")) {
    return { icon: ContactMail, color: "#DC2626", bg: alpha("#DC2626", 0.12) };
  }
  if (key.includes("field") || key.includes("visit")) {
    return { icon: Agriculture, color: "#16A34A", bg: alpha("#16A34A", 0.12) };
  }
  return { icon: TrendingUp, color: "#16A34A", bg: alpha("#16A34A", 0.12) };
};

const getMetricChange = (item) => {
  if (!item || typeof item !== "object") return null;
  const raw =
    item.change ??
    item.percentage_change ??
    item.percent_change ??
    item.growth ??
    item.growth_percentage ??
    item.vs_last_period;
  if (raw == null || raw === "") return null;
  const num = Number(raw);
  if (Number.isNaN(num)) return null;
  return num;
};

function formatCurrency(value) {
  if (value == null || isNaN(Number(value))) return "0";
  const num = Number(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
}

function percent(val) {
  if (val == null || isNaN(Number(val))) return "0%";
  return `${Number(val).toFixed(1)}%`;
}

function getTopFiveByRevenue(products) {
  const list = Array.isArray(products) ? [...products] : [];
  return list
    .sort((a, b) => Number(b.total_revenue || 0) - Number(a.total_revenue || 0))
    .slice(0, 5);
}

function getTopProductChartColors(theme) {
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || "#23C657";
  return [
    primary,
    alpha(primary, 0.82),
    secondary,
    theme.palette.success?.main || "#27CE88",
    alpha(secondary, 0.65),
  ];
}

const TopProductsRevenueDonut = ({ products, loading }) => {
  const theme = useTheme();
  const topFive = useMemo(() => getTopFiveByRevenue(products), [products]);
  const chartColors = useMemo(() => getTopProductChartColors(theme), [theme]);

  const { series, labels, totalRevenue, sharePercents } = useMemo(() => {
    const revenues = topFive.map((p) => Number(p.total_revenue || 0));
    const total = revenues.reduce((sum, val) => sum + val, 0);
    const shares = revenues.map((val) => (total > 0 ? (val / total) * 100 : 0));
    return {
      series: revenues,
      labels: topFive.map((p) => p.product__name || "Unknown"),
      totalRevenue: total,
      sharePercents: shares,
    };
  }, [topFive]);

  const donutChart = useMemo(
    () => ({
      options: {
        chart: {
          background: "transparent",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 750,
            animateGradually: { enabled: true, delay: 140 },
          },
        },
        labels,
        colors: chartColors,
        legend: { show: false },
        dataLabels: { enabled: false },
        stroke: {
          width: 3,
          colors: [theme.palette.background.paper],
        },
        states: {
          hover: { filter: { type: "lighten", value: 0.14 } },
          active: { filter: { type: "darken", value: 0.06 } },
        },
        theme: { mode: theme.palette.mode },
        tooltip: {
          fillSeriesColor: false,
          theme: theme.palette.mode === "dark" ? "dark" : "light",
          custom: ({ series: chartSeries, seriesIndex, w }) => {
            const value = chartSeries[seriesIndex];
            const label = w.globals.labels[seriesIndex];
            const color = w.config.colors[seriesIndex];
            const share =
              totalRevenue > 0 ? ((value / totalRevenue) * 100).toFixed(1) : "0.0";
            return [
              '<div style="padding:8px 12px;border-radius:8px;background:',
              color,
              ';color:#fff;font-weight:600;font-size:13px;box-shadow:0 4px 14px rgba(0,0,0,0.2);line-height:1.45;">',
              '<div style="font-size:12px;opacity:0.92;margin-bottom:2px;">',
              label,
              '</div>',
              '<div>',
              formatCurrency(value),
              '</div>',
              '<div style="font-size:11px;opacity:0.9;margin-top:2px;">',
              share,
              '% share</div></div>',
            ].join('');
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: { show: false },
                value: { show: false },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Total Revenue",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  formatter: () => formatCurrency(totalRevenue),
                },
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 600,
            options: {
              plotOptions: { pie: { donut: { size: "68%" } } },
            },
          },
        ],
      },
      series,
    }),
    [labels, series, chartColors, totalRevenue, theme]
  );

  if (loading) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={5} lg={4}>
          <Skeleton variant="circular" width={220} height={220} sx={{ mx: "auto" }} />
        </Grid>
        <Grid item xs={12} md={7} lg={8}>
          <Stack spacing={1.5}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: 1.5 }} />
            ))}
          </Stack>
        </Grid>
      </Grid>
    );
  }

  if (!topFive.length) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        <Small color="text.secondary">No product revenue data for the selected filters.</Small>
      </Box>
    );
  }

  return (
    <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
      <Grid item xs={12} md={5} lg={4}>
        <Box sx={{ maxWidth: 300, mx: "auto" }}>
          <StyledChart
            type="donut"
            height={280}
            options={donutChart.options}
            series={donutChart.series}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={7} lg={8}>
        <Stack spacing={1.25}>
          {topFive.map((row, idx) => {
            const revenue = Number(row.total_revenue || 0);
            const share = sharePercents[idx] ?? 0;
            const sliceColor = chartColors[idx % chartColors.length];
            return (
              <Stack
                key={`${row.product_id ?? row.product__sku}-${idx}`}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(sliceColor, 0.12)}`,
                  bgcolor: alpha(sliceColor, 0.04),
                  transition:
                    "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha(sliceColor, 0.1),
                    transform: "translateX(3px)",
                    boxShadow: `0 4px 12px ${alpha(sliceColor, 0.15)}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: sliceColor,
                    boxShadow: `0 0 0 3px ${alpha(sliceColor, 0.2)}`,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, lineHeight: 1.3 }}
                    noWrap
                    title={row.product__name}
                  >
                    {row.product__name}
                  </Typography>
                  <Tiny color="text.secondary" sx={{ display: "block" }}>
                    {formatCurrency(revenue)}
                  </Tiny>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    fontVariantNumeric: "tabular-nums",
                    minWidth: 44,
                    textAlign: "right",
                  }}
                >
                  {share.toFixed(1)}%
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Grid>
    </Grid>
  );
};

const InfoStat = ({ title, value, subtitle, color = "primary", compact = false, change }) => {
  const theme = useTheme();
  const visual = getMetricVisual(title);
  const Icon = visual.icon;
  const changeValue = change == null || Number.isNaN(Number(change)) ? null : Number(change);
  const isPositive = changeValue != null && changeValue > 0;
  const isNegative = changeValue != null && changeValue < 0;
  const ChangeIcon = isNegative ? TrendingDown : TrendingUp;

  return (
    <Card
      sx={{
        p: compact ? 1.5 : 2,
        height: "100%",
        minWidth: 0,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
        backgroundImage: "none",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: visual.bg,
            color: visual.color,
            flexShrink: 0,
            mt: 0.15,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Tiny sx={{ color: "text.secondary", fontWeight: 600, lineHeight: 1.3, display: "block" }}>
            {title}
          </Tiny>
          <Typography
            component="div"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: "-0.02em",
              fontVariantNumeric: "tabular-nums",
              fontSize: 13,
              lineHeight: 1.25,
              wordBreak: "break-word",
              mt: 0.35,
            }}
          >
            {value}
          </Typography>
          {changeValue != null ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.35,
                mt: 0.25,
                color: isPositive
                  ? "success.main"
                  : isNegative
                    ? "error.main"
                    : "text.secondary",
              }}
            >
              <ChangeIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" fontWeight={600}>
                {`${isPositive ? "+" : ""}${changeValue.toFixed(1)}% vs last period`}
              </Typography>
            </Box>
          ) : subtitle ? (
            <Small color={theme.palette[color]?.main || theme.palette.text.secondary}>{subtitle}</Small>
          ) : null}
        </Box>
      </Stack>
    </Card>
  );
};

const useLastNDays = (n = 7) => {
  return useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (n - 1));
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { startDate: fmt(start), endDate: fmt(end) };
  }, [n]);
};

const AnalyticsDashboard = () => {
  const theme = useTheme();
     const { setPageTitle } = usePageTitle();
  const [isBlinking, setIsBlinking] = useState(false);
  const isCompanyUser = apiService.isCompanyUser?.();
  const companyId = apiService.getCompanyId?.();
  const companyDetails = apiService.getCompanyDetails?.();
  useEffect(() => {
        setPageTitle('Dashboard');
    }, [setPageTitle]);
  // Filter states
  const [showFilters, setShowFilters] = useState(true);
  const [orderType, setOrderType] = useState('all'); // 'all', 'normal', 'bulk'
  const [companies, setCompanies] = useState([]);
  const [companiesMenuOpen, setCompaniesMenuOpen] = useState(false);
  const [pendingCompanyIds, setPendingCompanyIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(
    isCompanyUser && companyId ? [String(companyId)] : []
  );
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [salesAnalyticsType, setSalesAnalyticsType] = useState("daily");
  const [startDate, setStartDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [useDateFilter, setUseDateFilter] = useState(true); // New state to control date filter usage
  const getDefaultFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    return {
      fromDate: today,
      toDate: today,
      company: isCompanyUser && companyId ? [String(companyId)] : [],
      category: 'all',
      orderType: 'all',
    };
  };
  const [appliedFilters, setAppliedFilters] = useState(() => getDefaultFilters());

  const [loading, setLoading] = useState(true);
  const [salesOverview, setSalesOverview] = useState({});
  const [activityMetrics, setActivityMetrics] = useState({
    total_revenue: 0,
    full_payment: 0,
    outstanding_amount: 0,
    advance_payment: 0,
    credit: 0,
    total_activity: 0,
    activities: [],
  });
  const [byProduct, setByProduct] = useState({ category_performance: [], category_growth: [] });
  const [topProducts, setTopProducts] = useState([]);
  const [farmersAnalyticsData, setFarmersAnalyticsData] = useState([]);
  const [farmersAnalyticsLoading, setFarmersAnalyticsLoading] = useState(true);
  const [farmersAnalyticsError, setFarmersAnalyticsError] = useState("");
  const [farmersAnalyticsType, setFarmersAnalyticsType] = useState("monthly");
  const [error, setError] = useState("");
  const salesAnalyticsTypeOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];
  const farmersAnalyticsTypeOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  // Build query string with filters for analytics APIs
  const buildQueryString = (filters = appliedFilters, options = {}) => {
    const { includeAnalyticsType = false, analyticsType } = options;
    const params = new URLSearchParams();
    const {
      fromDate,
      toDate,
      company,
      category,
      orderType: selectedOrderType,
    } = filters;

    let companyParam = "";
    if (isCompanyUser) {
      companyParam = String(companyId || "");
    } else if (Array.isArray(company)) {
      companyParam = company.length > 0 ? company.join(",") : "";
    } else if (company && company !== "all") {
      companyParam = company;
    }
    if (companyParam) {
      params.set("company", companyParam);
    }
    if (category && category !== "all") {
      params.set("category", category);
    } else {
      params.set("category", "");
    }

    const apiOrderType = selectedOrderType === "normal" ? "app" : selectedOrderType;
    params.set("order_type", apiOrderType);

    if (includeAnalyticsType && analyticsType) {
      params.set("analytics_type", analyticsType);
    }

    if (fromDate) {
      params.set("from_date", fromDate);
    }
    if (toDate) {
      params.set("to_date", toDate);
    }

    return `?${params.toString()}`;
  };

  const fetchCompanies = async () => {
    try {
      if (isCompanyUser && companyDetails) {
        setCompanies([companyDetails]);
        return;
      }
      const response = await apiService.get("e-commerce/company/get-company-for-dashboard/");
      if (response?.success) {
        const rawCompanies = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.Company)
            ? response.data.Company
            : [];
        const mappedCompanies = rawCompanies
          .map((company, index) => ({
            id: company?.id ?? company?.company_id ?? company?.value ?? index,
            name:
              company?.name ??
              company?.company_name ??
              company?.label ??
              String(company?.value ?? ""),
          }))
          .filter((company) => company.name);
        setCompanies(mappedCompanies);
      } else {
        setCompanies([]);
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
      setCompanies([]);
    }
  };

  const getCategoryCompanyValue = (companySelection) => {
    if (isCompanyUser && companyId) return String(companyId);
    if (Array.isArray(companySelection)) {
      return companySelection.length === 1 ? companySelection[0] : "all";
    }
    return companySelection || "all";
  };

  const fetchCategories = async (companyValue = "all") => {
    try {
      let response;
      if (companyValue === "all") {
        response = await apiService.get(API_ENDPOINTS.GET_product_categories);
      } else {
        const userId = localStorage.getItem("user_id");
        response = await apiService.get(
          `${API_ENDPOINTS.GET_product_category}?company=${companyValue}&user_id=${userId}`
        );
      }
      if (response?.success) {
        setCategories(response?.data?.ProductCategory || []);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  const fetchSalesOverview = async (
    filters = appliedFilters,
    analyticsType = salesAnalyticsType
  ) => {
    const qs = buildQueryString(filters, { includeAnalyticsType: true, analyticsType });
    const overviewRes = await apiService.get(API_ENDPOINTS.GET_sales_revenue_overview + qs);
    setSalesOverview(overviewRes?.data || {});
  };

  const fetchFarmersAnalytics = async (
    filters = appliedFilters,
    analyticsType = farmersAnalyticsType
  ) => {
    setFarmersAnalyticsLoading(true);
    setFarmersAnalyticsError("");
    try {
      const qs = buildQueryString(filters, { includeAnalyticsType: true, analyticsType });
      const payload = await apiService.get(
        API_ENDPOINTS.GET_farmer_engagement_farmers_analytics + qs
      );
      const normalizedList = Array.isArray(payload?.data?.analytics)
        ? payload.data.analytics
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.analytics)
            ? payload.analytics
            : [];
      setFarmersAnalyticsData(normalizedList);
    } catch (err) {
      setFarmersAnalyticsError(
        typeof err === "string" ? err : err?.message || "Failed to load farmers analytics"
      );
      setFarmersAnalyticsData([]);
    } finally {
      setFarmersAnalyticsLoading(false);
    }
  };

  const fetchAllData = async (filters = appliedFilters) => {
    setLoading(true);
    setError("");
    try {
      const baseQs = buildQueryString(filters);
      const salesQs = buildQueryString(filters, {
        includeAnalyticsType: true,
        analyticsType: salesAnalyticsType,
      });
      const [activityRes, overviewRes, byProductRes, byCategoryRes] = await Promise.all([
        apiService.get(API_ENDPOINTS.GET_farmer_engagement_activity_metrics + baseQs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_overview + salesQs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_by_product + baseQs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_by_category + baseQs),
        fetchFarmersAnalytics(filters, farmersAnalyticsType),
      ]);
      setSalesOverview(overviewRes?.data || {});

      const metricsCandidates = [
        activityRes?.data?.data,
        activityRes?.data,
        activityRes,
      ].filter(Boolean);

      const pickMetricsPayload = metricsCandidates.find((payload) => {
        if (Array.isArray(payload?.activities) && payload.activities.length > 0) return true;
        return payload?.total_revenue != null || payload?.total_activity != null;
      }) || {};
      const rawActivities = pickMetricsPayload?.activities;
      const metricsActivities = Array.isArray(rawActivities)
        ? rawActivities
        : (rawActivities && typeof rawActivities === "object" ? Object.values(rawActivities) : []);
      const rawTotalRevenue = pickMetricsPayload?.total_revenue ?? 0;
      const derivedTotalActivity = pickMetricsPayload?.total_activity != null
        ? Number(pickMetricsPayload.total_activity || 0)
        : metricsActivities.reduce((sum, item) => sum + Number(item?.value || 0), 0);
      setActivityMetrics({
        total_revenue: Number(rawTotalRevenue || 0),
        full_payment: Number(pickMetricsPayload?.full_payment || 0),
        outstanding_amount: Number(pickMetricsPayload?.outstanding_amount || 0),
        advance_payment: Number(pickMetricsPayload?.advance_payment || 0),
        credit: Number(pickMetricsPayload?.credit || 0),
        total_activity: derivedTotalActivity,
        activities: metricsActivities,
      });

      // category performance/growth comes from category endpoint
      setByProduct({
        category_performance: Array.isArray(byCategoryRes?.data?.category_performance)
          ? byCategoryRes.data.category_performance
          : Array.isArray(byCategoryRes?.category_performance)
            ? byCategoryRes.category_performance
            : [],
        category_growth: Array.isArray(byCategoryRes?.data?.category_growth)
          ? byCategoryRes.data.category_growth
          : Array.isArray(byCategoryRes?.category_growth)
            ? byCategoryRes.category_growth
            : [],
      });
      // top products is an array from top-products endpoint
      setTopProducts(Array.isArray(byProductRes?.data) ? byProductRes.data : []);
    } catch (err) {
      console.error(err);
      setError(typeof err === "string" ? err : err?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData(appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    const initializeFilters = async () => {
      await fetchCompanies();
      const initialCompany = isCompanyUser && companyId ? [String(companyId)] : selectedCompany;
      await fetchCategories(getCategoryCompanyValue(initialCompany));
    };
    initializeFilters();
  }, []);

  const handleApplyFilters = () => {
    const nextAppliedFilters = {
      fromDate: useDateFilter ? startDate : "",
      toDate: useDateFilter ? endDate : "",
      company: isCompanyUser && companyId ? [String(companyId)] : selectedCompany,
      category: selectedCategory,
      orderType,
    };
    setAppliedFilters(nextAppliedFilters);
  };

  const handleCompanyChange = (event) => {
    const value = event.target.value;
    const companyIds = typeof value === "string" ? value.split(",") : value;
    setPendingCompanyIds(companyIds);
  };

  const applyCompanySelection = async (companyIds) => {
    setSelectedCompany(companyIds);
    setSelectedCategory("all");
    await fetchCategories(getCategoryCompanyValue(companyIds));
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleOrderTypeChange = (event) => {
    setOrderType(event.target.value);
  };

  const handleRefresh = () => {
    fetchAllData();
  };

  const handleResetFilters = () => {
    const defaults = getDefaultFilters();
    setStartDate(defaults.fromDate);
    setEndDate(defaults.toDate);
    setOrderType(defaults.orderType);
    setSalesAnalyticsType("daily");
    setSelectedCompany(defaults.company);
    setSelectedCategory(defaults.category);
    setUseDateFilter(true); // keep existing behavior
    setAppliedFilters(defaults);
    setTimeout(() => fetchCategories(getCategoryCompanyValue(defaults.company)), 0);
  };

  // Trigger a quick blink effect when Reset/Refresh is clicked
  const handleResetFiltersWithBlink = () => {
    setIsBlinking(true);
    // keep blink brief and let existing logic run
    handleResetFilters();
    setTimeout(() => setIsBlinking(false), 420);
  };

  const handleClearDateFilter = () => {
    setUseDateFilter(false);
    setStartDate('');
    setEndDate('');
  };

  const handleEnableDateFilter = () => {
    setUseDateFilter(true);
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
  };

  const getOrderTypeLabel = (type) => {
    switch (type) {
      case 'normal': return 'Orders';
      case 'bulk': return 'Bulk Orders';
      case 'all': return 'All Orders';
      default: return 'All Orders';
    }
  };

  const isFiltersActive = () => {
    const today = new Date().toISOString().split('T')[0];
    return orderType !== 'all' || 
           !useDateFilter ||
           (useDateFilter && (startDate !== today || 
            endDate !== today));
  };

  const ordersByStatusChart = useMemo(() => {
    const statuses = salesOverview?.orders_by_status || [];
    const labels = statuses.map((s) => s.status);
    const series = statuses.map((s) => Number(s.count || 0));
    return {
      options: {
        chart: { background: "transparent" },
        labels,
        colors: [theme.palette.primary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main, theme.palette.error.main, theme.palette.grey[500], theme.palette.secondary.main],
        legend: { show: true, position: "bottom", fontSize: "13px", fontWeight: 500 },
              theme: { mode: theme.palette.mode },
      tooltip: {
        fillSeriesColor: false,
        theme: 'dark',
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series[seriesIndex];
          const label = w.globals.labels[seriesIndex];
          const color = w.config.colors[seriesIndex]; // 👈 get the same color as the slice
          return `
            <div style="
              padding: 6px 10px;
              border-radius: 6px;
              background: ${color};
              color: #fff;
              font-weight: 600;
              font-size: 13px;
              box-shadow: 0 2px 6px rgba(0,0,0,0.25);
            ">
              ${label}: ${value}
            </div>
          `;
        },
      },


        plotOptions: {
          pie: {
            donut: {
              size: "60%",
              labels: { show: true, total: { show: true, label: "Orders" } },
            },
          },
        },
      },
      series,
    };
  }, [salesOverview, theme]);

  const dailyRevenueChart = useMemo(() => {
    const salesAnalytics = salesOverview?.sales_analytics || [];
    
    // Use the pre-aggregated data directly from API
    const chartData = salesAnalytics.map((item) => ({
      label: item.label || "",
      revenue: Number(item.revenue || 0),
      orders: Number(item.orders || 0),
    }));

    // Data is already aggregated from API, just use it directly
    const categories = chartData.map((d) => d.label);
    const revenue = chartData.map((d) => d.revenue);
    const orders = chartData.map((d) => d.orders);

    return {
      options: {
        chart: { background: "transparent", toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 4, hover: { size: 6 } },
        colors: [theme.palette.primary.main, theme.palette.info.main],
        xaxis: {
          type: "category",
          categories,
          labels: { style: { colors: theme.palette.text.disabled, fontFamily: theme.typography.fontFamily, fontWeight: 500 } },
        },
        yaxis: [{
          labels: { style: { colors: theme.palette.text.secondary } },
          title: { text: "Revenue" },
        }, {
          opposite: true,
          labels: { style: { colors: theme.palette.text.secondary } },
          title: { text: "Orders" },
        }],
        theme: { mode: theme.palette.mode },
        tooltip: {
          shared: false,
          intersect: true,
          custom: ({ dataPointIndex }) => {
            const point = chartData[dataPointIndex] || { label: "", revenue: 0, orders: 0 };
            return `
              <div style="padding:8px 10px;border-radius:6px;background:${theme.palette.background.paper};border:1px solid ${theme.palette.divider};min-width:130px;">
                <div style="font-size:12px;font-weight:600;margin-bottom:4px;color:${theme.palette.text.primary};">${point.label}</div>
                <div style="font-size:12px;color:${theme.palette.text.secondary};">Revenue: ${formatCurrency(point.revenue)}</div>
                <div style="font-size:12px;color:${theme.palette.text.secondary};">Orders: ${point.orders} orders</div>
              </div>
            `;
          },
        },
      },
      series: [
        { name: "Revenue", type: "area", data: revenue },
        { name: "Orders", type: "line", data: orders },
      ],
    };
  }, [salesOverview, theme]);

const categoryRevenueChart = useMemo(() => {
  const DEFAULT_CATEGORY_LIMIT = 10;
  const perf = byProduct?.category_performance || [];
  const activities = Array.isArray(activityMetrics?.activities) ? activityMetrics.activities : [];
  const hasCategoryData = perf.length > 0;
  const limitedPerf = hasCategoryData ? perf.slice(0, DEFAULT_CATEGORY_LIMIT) : [];
  const limitedActivities = hasCategoryData ? [] : activities.slice(0, DEFAULT_CATEGORY_LIMIT);
  const categories = hasCategoryData
    ? limitedPerf.map((p) => p.category || "N/A")
    : limitedActivities.map((a) => a.label || "N/A");
  const revenue = hasCategoryData
    ? limitedPerf.map((p) => Number(p.total_revenue || 0))
    : limitedActivities.map((a) => Number(a.value || 0));
  const quantity = hasCategoryData ? limitedPerf.map((p) => Number(p.total_quantity || 0)) : [];

  return {
    options: {
      chart: {
        background: "transparent",
        toolbar: { show: false },
      },
      stroke: {
        width: [0, 3],
        curve: "smooth",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "45%",
          dataLabels: { position: "top" },
        },
      },
      dataLabels: { enabled: false },
      colors: [theme.palette.primary.main, theme.palette.warning.main],
      grid: {
        borderColor: theme.palette.divider,
        strokeDashArray: 4,
        padding: { left: 10, right: 10 },
      },
      xaxis: {
        categories,
        tickPlacement: "on",
        labels: {
          show: true,
          rotate: -45,
          hideOverlappingLabels: false,
          trim: false,
          style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          formatter: (value) =>
            value && value.length > 15 ? `${value.slice(0, 15)}...` : value,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: hasCategoryData ? [
        {
          title: { text: "Revenue (₹)" },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
            formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val),
          },
        },
        {
          opposite: true,
          title: { text: "Quantity" },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          },
        },
      ] : [
        {
          title: { text: "Count" },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          },
        },
      ],
      legend: {
        position: "bottom",
        labels: { colors: theme.palette.text.secondary },
        fontSize: "12px",
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          formatter: (value, { dataPointIndex }) => {
            // Show full category name in tooltip
            return categories[dataPointIndex] || value;
          },
        },
        y: {
          formatter: (val, { seriesIndex }) =>
            !hasCategoryData ? `${val}` : (seriesIndex === 0 ? formatCurrency(val) : `${val}`),
        },
      },
      theme: { mode: theme.palette.mode },
      responsive: [
        {
          breakpoint: 992,
          options: {
            plotOptions: { bar: { columnWidth: "60%" } },
            xaxis: {
              labels: {
                rotate: -60,
                style: { fontSize: "10px" },
                formatter: (value) =>
                  value && value.length > 10 ? `${value.slice(0, 10)}...` : value,
              },
            },
            legend: { fontSize: "10px" },
          },
        },
        {
          breakpoint: 576,
          options: {
            plotOptions: { bar: { columnWidth: "80%" } },
            xaxis: {
              labels: {
                rotate: -90,
                style: { fontSize: "9px" },
                formatter: (value) =>
                  value && value.length > 8 ? `${value.slice(0, 8)}...` : value,
              },
            },
            legend: { position: "top" },
          },
        },
      ],
    },
    series: hasCategoryData
      ? [
          { name: "Revenue", type: "column", data: revenue },
          { name: "Quantity", type: "line", data: quantity },
        ]
      : [
          { name: "Activity", type: "column", data: revenue },
        ],
  };
}, [byProduct, activityMetrics, theme]);




  const categoryGrowthChart = useMemo(() => {
    const growth = byProduct?.category_growth || [];
    const categories = Array.from(new Set(growth.map((g) => (g.month || "").slice(0, 7))));
    const categoryNames = Array.from(new Set(growth.map((g) => g.product__category__name)));
    const series = categoryNames.map((name) => ({
      name,
      data: categories.map((m) => {
        const row = growth.find((g) => g.product__category__name === name && (g.month || "").startsWith(m));
        return Number(row?.revenue || 0);
      }),
    }));
    return {
      options: {
        chart: { background: "transparent", toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        colors: [theme.palette.primary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main, theme.palette.secondary.main, theme.palette.error.main],
        xaxis: { categories, labels: { style: { colors: theme.palette.text.secondary } } },
        yaxis: { labels: { style: { colors: theme.palette.text.secondary } }, title: { text: "Revenue" } },
        legend: { position: "bottom" },
        theme: { mode: theme.palette.mode },
        tooltip: { y: { formatter: (val) => formatCurrency(val) } },
      },
      series,
    };
  }, [byProduct, theme]);

  const farmersAnalyticsChart = useMemo(() => {
    const normalizedRows = Array.isArray(farmersAnalyticsData) ? farmersAnalyticsData : [];
    const analyticsRows = normalizedRows.filter((row) => row && typeof row === "object");
    const categories = analyticsRows.map((item, index) => item?.label || `Item ${index + 1}`);
    const totalFarmers = analyticsRows.map((item) => Number(item?.total_farmers || 0));
    const activeFarmers = analyticsRows.map((item) => Number(item?.active_farmers || 0));
    const newRegistrations = analyticsRows.map((item) => Number(item?.new_registrations || 0));
    const repeatOrderingFarmers = analyticsRows.map((item) =>
      Number(item?.repeat_ordering_farmers || 0)
    );

    return {
      hasData: categories.length > 0,
      options: {
        chart: { background: "transparent", toolbar: { show: false } },
        dataLabels: { enabled: false },
        colors: [
          theme.palette.primary.main,
          theme.palette.success.main,
          theme.palette.success.dark || theme.palette.success.main,
          theme.palette.secondary.main,
        ],
        legend: {
          show: true,
          position: "bottom",
          labels: { colors: theme.palette.text.secondary },
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "58%",
          },
        },
        grid: {
          borderColor: theme.palette.divider,
          strokeDashArray: 4,
        },
        xaxis: {
          categories,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          },
        },
        yaxis: {
          labels: { style: { colors: theme.palette.text.secondary, fontSize: "12px" } },
        },
        tooltip: {
          shared: false,
          intersect: true,
          y: {
            formatter: (val) => `${Number(val || 0)}`,
          },
        },
        theme: { mode: theme.palette.mode },
      },
      series: [
        { name: "Total Farmers", data: totalFarmers },
        { name: "Active Farmers", data: activeFarmers },
        { name: "New Registrations", data: newRegistrations },
        { name: "Repeat Ordering Farmers", data: repeatOrderingFarmers },
      ],
    };
  }, [farmersAnalyticsData, theme]);

  return (
    <Box
      pt={1.5}
      pb={3}
      px={{ xs: 0.5, sm: 1 }}
      sx={{
        animation: isBlinking ? "blink 420ms ease-in-out" : "none",
        "@keyframes blink": {
          "0%": { opacity: 1 },
          "50%": { opacity: 0 },
          "100%": { opacity: 1 },
        },
      }}
    >
      {error ? (
        <Card
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "none",
          }}
        >
          <Small color="error.main">{error}</Small>
        </Card>
      ) : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          {/* Filters & Search **/}
          <FilterContainer sx={{ mb: 0 }}>
            <FilterHeader>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
                  <FilterList color="primary" />
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ letterSpacing: "-0.01em", color: "text.primary" }}
                  >
                    Filters & Search
                  </Typography>
                  {useDateFilter && startDate && endDate && (
                    <>
                      <StatusChip statuscolor={theme.palette.success.main}>Active</StatusChip>
                      <StatusChip statuscolor={theme.palette.text.secondary}>
                        <CalendarToday sx={{ fontSize: 13 }} />
                        {new Date(startDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}{" "}
                        -{" "}
                        {new Date(endDate).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </StatusChip>
                    </>
                  )}
                </Box>
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                  sx={{
                    transform: showFilters ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                  }}
                >
                  <ExpandMore />
                </IconButton>
              </Box>
            </FilterHeader>

            <Collapse in={showFilters}>
              <FilterContent>
                {/* <Typography variant="caption" sx={{ mb: 1, display: "block", fontWeight: 600, color: "text.secondary" }}>
                  Date Range Filter
                </Typography> */}
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 1.5,
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "repeat(3, minmax(0, 1fr))",
                          lg: "repeat(5, minmax(0, 1fr))",
                        },
                        alignItems: "center",
                        "& > *": {
                          minWidth: 0,
                        },
                        "& .MuiFormControl-root, & .MuiTextField-root": {
                          minWidth: { xs: "100%", sm: 0 },
                        },
                      }}
                    >
                      <DatePickerField
                        fullWidth
                        label="From Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setUseDateFilter(true);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          max: endDate || new Date().toISOString().split('T')[0],
                        }}
                        size="small"
                      />
                      <DatePickerField
                        fullWidth
                        label="To Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setUseDateFilter(true);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: startDate || '',
                          max: new Date().toISOString().split('T')[0],
                        }}
                        size="small"
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel id="analytics-companies-label">Company</InputLabel>
                        <Select
                          labelId="analytics-companies-label"
                          multiple
                          value={companiesMenuOpen ? pendingCompanyIds : selectedCompany}
                          label="Company"
                          open={companiesMenuOpen}
                          onOpen={() => {
                            setPendingCompanyIds(
                              Array.isArray(selectedCompany) ? selectedCompany : []
                            );
                            setCompaniesMenuOpen(true);
                          }}
                          onClose={() => setCompaniesMenuOpen(false)}
                          onChange={handleCompanyChange}
                          disabled={isCompanyUser}
                          sx={{
                            borderRadius: 2.5,
                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              overflow: "visible",
                              py: 0.75,
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: { maxHeight: 320 },
                            },
                          }}
                          renderValue={(selected) => {
                            if (!selected?.length) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  All Companies
                                </Typography>
                              );
                            }
                            const visibleIds = selected.slice(0, 2);
                            const hiddenCount = selected.length - visibleIds.length;
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "nowrap",
                                  gap: 0.5,
                                  alignItems: "center",
                                  width: "100%",
                                  minWidth: 0,
                                }}
                              >
                                {visibleIds.map((id) => {
                                  const company = companies.find((c) => String(c.id) === String(id));
                                  return (
                                    <Chip
                                      key={id}
                                      label={company?.name || id}
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      sx={{
                                        height: 24,
                                        fontSize: "0.8125rem",
                                        maxWidth: hiddenCount > 0 ? 100 : 150,
                                        flexShrink: 1,
                                        minWidth: 0,
                                        "& .MuiChip-label": {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        },
                                      }}
                                    />
                                  );
                                })}
                                {hiddenCount > 0 && (
                                  <Chip
                                    size="small"
                                    label={`+${hiddenCount} more`}
                                    variant="filled"
                                    sx={{
                                      height: 24,
                                      fontSize: "0.8125rem",
                                      flexShrink: 0,
                                      fontWeight: 600,
                                      maxWidth: "none",
                                      bgcolor: theme.palette.primary.main,
                                      color: theme.palette.primary.contrastText,
                                      "& .MuiChip-label": {
                                        color: theme.palette.primary.contrastText,
                                        px: 1,
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                            );
                          }}
                        >
                          {companies.map((company) => {
                            const companyIdStr = String(company.id);
                            const checked =
                              Array.isArray(pendingCompanyIds) &&
                              pendingCompanyIds.includes(companyIdStr);
                            return (
                              <MenuItem key={company.id} value={companyIdStr}>
                                <Checkbox checked={checked} size="small" sx={{ py: 0 }} />
                                <ListItemText primary={company.name} />
                              </MenuItem>
                            );
                          })}
                          {!isCompanyUser && companies.length > 0 && (
                            <ListSubheader
                              component="div"
                              disableSticky
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                position: "sticky",
                                bottom: 0,
                                zIndex: 1,
                                backgroundColor: "background.paper",
                                borderTop: "1px solid",
                                borderColor: "divider",
                                py: 1,
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    applyCompanySelection(pendingCompanyIds);
                                    setCompaniesMenuOpen(false);
                                  }}
                                >
                                  Add
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPendingCompanyIds([]);
                                    applyCompanySelection([]);
                                    setCompaniesMenuOpen(false);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </ListSubheader>
                          )}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={selectedCategory}
                          label="Category"
                          onChange={handleCategoryChange}
                          sx={{ borderRadius: 2.5 }}
                        >
                          <MenuItem value="all">All Categories</MenuItem>
                          {categories.map((category) => (
                            <MenuItem key={category.id} value={String(category.id)}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <InputLabel>Order Type</InputLabel>
                        <Select
                          value={orderType}
                          label="Order Type"
                          onChange={handleOrderTypeChange}
                          sx={{ borderRadius: 2.5 }}
                        >
                          <MenuItem value="all">All Orders</MenuItem>
                          <MenuItem value="normal">App Order</MenuItem>
                          <MenuItem value="bulk">Bulk Order</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.25, flexWrap: "wrap" }}>
                      <ActionButton
                        sx={{
                          minWidth: 120,
                          borderColor: "divider",
                          color: "text.primary",
                          bgcolor: "background.paper",
                          "&:hover": {
                            borderColor: "grey.400",
                            backgroundColor: alpha(theme.palette.grey[500], 0.04),
                          },
                        }}
                        variant="outlined"
                        startIcon={
                          <Refresh
                            sx={{
                              animation: loading ? "spin 1s linear infinite" : "none",
                              "@keyframes spin": {
                                "0%": { transform: "rotate(0deg)" },
                                "100%": { transform: "rotate(360deg)" },
                              },
                            }}
                          />
                        }
                        onClick={handleResetFiltersWithBlink}
                      >
                        Refresh
                      </ActionButton>
                      <ActionButton
                        sx={{
                          minWidth: 140,
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          "&:hover": {
                            bgcolor: "primary.dark",
                          },
                        }}
                        variant="contained"
                        startIcon={<FilterList />}
                        onClick={handleApplyFilters}
                      >
                        Apply Filters
                      </ActionButton>
                    </Box>
                  </Grid>
                </Grid>
              </FilterContent>
            </Collapse>
          </FilterContainer>
        </Grid>

        <Grid item xs={12}>
          {(() => {
            const activities = Array.isArray(activityMetrics?.activities)
              ? activityMetrics.activities
              : [];
            const farmersIndex = activities.findIndex(
              (item) => String(item?.label || "").trim().toLowerCase() === "total farmers"
            );
            const firstActivities =
              farmersIndex >= 0
                ? activities
                    .slice(0, farmersIndex)
                    .filter((item) => String(item?.label || "").trim().toLowerCase() !== "total orders")
                : activities;
            let secondActivities =
              farmersIndex >= 0 ? activities.slice(farmersIndex) : [];
            const ordersCard = activities.find(
              (item) => String(item?.label || "").trim().toLowerCase() === "total orders"
            );
            const farmersInSecond = secondActivities.findIndex(
              (item) => String(item?.label || "").trim().toLowerCase() === "total farmers"
            );
            const ordersInSecond = secondActivities.findIndex(
              (item) => String(item?.label || "").trim().toLowerCase() === "total orders"
            );
            if (ordersCard && ordersInSecond < 0 && farmersInSecond >= 0) {
              secondActivities.splice(farmersInSecond + 1, 0, ordersCard);
            } else if (farmersInSecond >= 0 && ordersInSecond >= 0 && farmersInSecond !== ordersInSecond - 1) {
              const [farmersCard] = secondActivities.splice(farmersInSecond, 1);
              const nextOrdersIndex = secondActivities.findIndex(
                (item) => String(item?.label || "").trim().toLowerCase() === "total orders"
              );
              secondActivities.splice(nextOrdersIndex, 0, farmersCard);
            }
            const firstRowCount = 5 + firstActivities.length;
            const gridSx = {
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
                lg: `repeat(${firstRowCount}, minmax(0, 1fr))`,
              },
              gap: 1.5,
              width: "100%",
              alignItems: "stretch",
            };
            const renderActivity = (item, idx) => (
              <InfoStat
                compact
                key={`${item.label || "activity"}-${idx}`}
                title={item.label || "Activity"}
                value={item.value ?? 0}
                change={getMetricChange(item)}
              />
            );
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
                <Box sx={gridSx}>
                  <InfoStat
                    compact
                    title="Total Revenue"
                    value={formatCurrency(activityMetrics?.total_revenue)}
                    change={getMetricChange(activityMetrics)}
                  />
                  <InfoStat compact title="Full Payment" value={formatCurrency(activityMetrics?.full_payment)} />
                  <InfoStat
                    compact
                    title="Advance Payment"
                    value={formatCurrency(activityMetrics?.advance_payment)}
                  />
                  <InfoStat
                    compact
                    title="Outstanding Amount"
                    value={formatCurrency(activityMetrics?.outstanding_amount)}
                  />
                  <InfoStat compact title="Credit" value={formatCurrency(activityMetrics?.credit)} />
                  {firstActivities.map(renderActivity)}
                </Box>
                {secondActivities.length > 0 && (
                  <Box sx={gridSx}>
                    {secondActivities.map((item, idx) => renderActivity(item, idx + firstActivities.length))}
                  </Box>
                )}
              </Box>
            );
          })()}
        </Grid>

        <Grid item xs={12} md={8}>
          <DashboardSectionCard sx={{ p: 2.5 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              gap={1}
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: "-0.01em" }}>
                Sales Analytics
              </Typography>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={salesAnalyticsType}
                  label="Period"
                  onChange={(event) => {
                    const nextType = event.target.value;
                    setSalesAnalyticsType(nextType);
                    fetchSalesOverview(appliedFilters, nextType);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  {salesAnalyticsTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <StyledChart
              type="area"
              height={300}
              options={{
                ...dailyRevenueChart.options,
                fill: {
                  type: "gradient",
                  gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.35,
                    opacityTo: 0.05,
                    stops: [0, 90, 100],
                  },
                },
                stroke: { ...(dailyRevenueChart.options.stroke || {}), curve: "smooth", width: 3 },
                colors: [theme.palette.primary.main, theme.palette.secondary?.main || "#7C3AED"],
                grid: {
                  borderColor: theme.palette.divider,
                  strokeDashArray: 4,
                },
              }}
              series={dailyRevenueChart.series}
            />
          </DashboardSectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <DashboardSectionCard sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: "-0.01em" }}>
                Orders by Status
              </Typography>
              {/* <Chip
                size="small"
                label="This Period.."
                sx={{
                  fontWeight: 600,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                }}
              /> */}
            </Stack>
            <StyledChart
              type="donut"
              height={300}
              options={ordersByStatusChart.options}
              series={ordersByStatusChart.series}
            />
          </DashboardSectionCard>
        </Grid>

        {/* <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <H6>{(byProduct?.category_performance || []).length > 0 ? "Revenue and Quantity by Category" : "Activity by Type"}</H6>
            <StyledChart type="line" height={340} options={categoryRevenueChart.options} series={categoryRevenueChart.series} />
          </Card>
        </Grid> */}

        <Grid item xs={12}>
          <DashboardSectionCard sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: "-0.01em", mb: 1 }}>
              Top Products by Sales
            </Typography>
            <Box mt={1}>
              <TopProductsRevenueDonut products={topProducts} loading={loading} />
            </Box>
          </DashboardSectionCard>
        </Grid>

        <Grid item xs={12}>
          <DashboardSectionCard sx={{ p: 2.5 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              gap={1}
            >
              <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: "-0.01em" }}>
                Farmers Analytics
              </Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Period</InputLabel>
                <Select
                  value={farmersAnalyticsType}
                  label="Period"
                  onChange={(event) => {
                    const nextType = event.target.value;
                    setFarmersAnalyticsType(nextType);
                    fetchFarmersAnalytics(appliedFilters, nextType);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  {farmersAnalyticsTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Box mt={1}>
              {farmersAnalyticsLoading ? (
                <Skeleton variant="rounded" height={300} sx={{ borderRadius: 1.5 }} />
              ) : farmersAnalyticsError ? (
                <Box
                  sx={{
                    py: 6,
                    textAlign: "center",
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.04),
                    border: `1px dashed ${alpha(theme.palette.error.main, 0.2)}`,
                  }}
                >
                  <Small color="error.main">{farmersAnalyticsError}</Small>
                </Box>
              ) : farmersAnalyticsChart.hasData ? (
                <StyledChart
                  key={`farmers-${farmersAnalyticsType}-${appliedFilters.fromDate}-${appliedFilters.toDate}-${appliedFilters.orderType}-${appliedFilters.category}-${appliedFilters.company}`}
                  type="bar"
                  height={320}
                  options={farmersAnalyticsChart.options}
                  series={farmersAnalyticsChart.series}
                />
              ) : (
                <Box
                  sx={{
                    py: 6,
                    textAlign: "center",
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}
                >
                  <Small color="text.secondary">No farmers analytics data available.</Small>
                </Box>
              )}
            </Box>
          </DashboardSectionCard>
        </Grid>

      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard; 